import { useEffect, useMemo, useState } from "react";
import { navItems, contactInfo } from "@/mocks/home";
import { useScrollSpy } from "@/hooks/useScrollSpy";

export default function Header() {
  const ids = useMemo(() => navItems.map((item) => item.id), []);
  const activeId = useScrollSpy(ids, 140);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-background-50/95 backdrop-blur-md shadow-[0_1px_0_0_oklch(var(--background-200))]"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-8">
          <nav className="flex h-[72px] items-center justify-between" aria-label="주요 메뉴">
            <a href="#top" className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-macbook-line text-lg"></i>
              </span>
              <span
                className={`font-heading text-xl font-black tracking-tight transition-colors ${
                  scrolled ? "text-foreground-950" : "text-background-50"
                }`}
              >
                맥픽스
              </span>
            </a>

            <ul className="hidden items-center gap-1 xl:flex">
              {navItems.map((item) => {
                const active = activeId === item.id;
                return (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className={`relative inline-flex items-center whitespace-nowrap rounded-md px-3.5 py-2 font-label text-[15px] font-bold transition-colors ${
                        active
                          ? scrolled
                            ? "bg-primary-100 text-primary-700"
                            : "bg-background-50/15 text-background-50"
                          : scrolled
                            ? "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
                            : "text-background-200 hover:bg-background-50/10 hover:text-background-50"
                      }`}
                    >
                      {item.label}
                      {active && (
                        <span className="absolute -bottom-0.5 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-primary-500"></span>
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="hidden items-center gap-3 xl:flex">
              <a
                href={contactInfo.phoneHref}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-phone-fill text-base"></i>
                {contactInfo.phone}
              </a>
            </div>

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="메뉴 열기"
              className={`flex h-11 w-11 items-center justify-center rounded-md transition-colors xl:hidden ${
                scrolled
                  ? "text-foreground-950 hover:bg-background-100"
                  : "text-background-50 hover:bg-background-50/10"
              }`}
            >
              <i className="ri-menu-3-line text-2xl"></i>
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile menu (global) */}
      <div
        className={`fixed inset-0 z-[60] xl:hidden ${menuOpen ? "" : "pointer-events-none"}`}
        aria-hidden={!menuOpen}
      >
        <div
          onClick={() => setMenuOpen(false)}
          className={`absolute inset-0 bg-foreground-950/50 transition-opacity duration-300 ${
            menuOpen ? "opacity-100" : "opacity-0"
          }`}
        ></div>
        <div
          className={`absolute right-0 top-0 h-full w-[82%] max-w-sm bg-background-50 px-6 py-6 transition-transform duration-300 ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="mb-8 flex items-center justify-between">
            <span className="font-heading text-lg font-black text-foreground-950">메뉴</span>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="메뉴 닫기"
              className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          <ul className="flex flex-col gap-1.5">
            {navItems.map((item) => {
              const active = activeId === item.id;
              return (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={() => setMenuOpen(false)}
                    className={`flex items-center justify-between rounded-lg px-4 py-3.5 font-label text-base font-bold transition-colors ${
                      active
                        ? "bg-primary-100 text-primary-700"
                        : "text-foreground-800 hover:bg-background-100"
                    }`}
                  >
                    {item.label}
                    <i
                      className={`${
                        active ? "ri-arrow-right-line text-primary-600" : "ri-arrow-right-s-line text-foreground-400"
                      }`}
                    ></i>
                  </a>
                </li>
              );
            })}
          </ul>
          <a
            href={contactInfo.phoneHref}
            className="mt-8 flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3.5 font-label text-base font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-phone-fill text-lg"></i>
            전화 상담 {contactInfo.phone}
          </a>
        </div>
      </div>
    </>
  );
}